import { supabase, SUPABASE_URL } from '../../../shared/supabase';
import {
  createNotification,
  createStaffNotifications,
} from '../../../shared/api/notificationService';
import { logActivity } from '../../../shared/api/activityLogService';
import { sendOrderStatusEmail, sendBillToCustomer } from '../../../shared/api/emailService';
import { offlineStorage } from '../../../shared/utils/offlineStorage';
import { isGuestPlaceholderEmail } from '../../../customer/api/paymentService';

export const STATUS_ORDER = {
  pending: 0,
  preparing: 1,
  ready: 2,
  completed: 3,
  rejected: 4,
};

class OrderService {
  constructor() {
    this.STATUS_ORDER = STATUS_ORDER;
  }

  /**
   * Confirm that an offline payment (Pay at Restaurant / manual UPI) was
   * collected. Marks the order's payment as completed via a SECURITY DEFINER
   * RPC that verifies the caller manages the restaurant and that the order is a
   * pending offline order. The client never writes payment_status directly.
   */
  async confirmOfflinePayment(orderId, note = null) {
    const { data, error } = await supabase.rpc('confirm_offline_order_payment', {
      p_order_id: orderId,
      p_note: note,
    });
    if (error) throw error;
    return data;
  }

  /**
   * Process refund for an order via Razorpay Edge Function
   */
  async processRefund(order) {
    try {
      if (!order.razorpay_payment_id) {
        console.warn('Cannot process refund: No Razorpay payment ID found');
        return { success: false, error: 'No payment ID found for refund' };
      }

      if (order.payment_status !== 'completed') {
        console.warn('Cannot process refund: Payment status is not completed');
        return { success: false, error: 'Payment was not completed' };
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/process-refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          payment_id: order.razorpay_payment_id,
          order_id: order.id,
          amount: order.total,
          notes: {
            order_id: order.id,
            order_number: order.order_number,
            reason: order.cancel_reason || 'Order rejected by restaurant',
          },
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Refund processing failed:', result);
        return { success: false, error: result.error || 'Refund failed' };
      }

      return { success: true, refund: result.refund };
    } catch (error) {
      console.error('Error processing refund:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Helper function to create admin notification for new orders
   * @private
   */
  async _createManagerOrderNotification(order) {
    try {
      await createStaffNotifications(
        {
          title: 'New Order',
          message: `New order #${order.id} received`,
          type: 'order_received',
          order_id: order.id,
          metadata: { order: order },
        },
        order.restaurant_id,
      );
    } catch (error) {
      console.error('[orderService] Failed to create manager order notification:', error);
    }
  }

  /**
   * Get orders by user ID
   */
  async getUserOrders(userId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all orders for a specific restaurant
   */
  async getRestaurantOrders(restaurantId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Helper function to create user notification for order status updates
   * @private
   */
  async _createOrderStatusNotification(order, refundResult = null) {
    try {
      if (!order.user_id) {
        console.warn('[Notification] Skipping notification - Order has no user_id (guest order)');
        return;
      }

      let title, message, type;

      switch (order.status) {
        case 'confirmed':
          title = 'Order Accepted';
          message = `Your order #${order.order_number} has been accepted`;
          type = 'order_accepted';
          break;
        case 'preparing':
          title = 'Order Preparing';
          message = `Your order #${order.order_number} is now being prepared`;
          type = 'order_preparing';
          break;
        case 'ready':
          title = 'Order Ready';
          message = `Your order #${order.order_number} is ready for pickup`;
          type = 'order_ready';
          break;
        case 'rejected':
          title = 'Order Rejected';
          if (refundResult?.success && refundResult.refund) {
            message = `Your order #${order.order_number} has been rejected. A refund of ₹${refundResult.refund.amount} has been initiated and will be credited to your account within 5-7 business days.`;
          } else if (order.payment_status === 'completed') {
            message = `Your order #${order.order_number} has been rejected. Your refund is being processed and will be credited soon.`;
          } else {
            message = `Your order #${order.order_number} has been rejected`;
          }
          type = 'order_rejected';
          break;
        case 'completed':
          title = 'Order Completed';
          message = `Your order #${order.order_number} has been completed`;
          type = 'order_ready';
          break;
        default:
          title = `Order ${order.status}`;
          message = `Your order #${order.order_number} status: ${order.status}`;
          type = 'order_received';
      }

      const metadata = { order_status: order.status };
      if (refundResult?.success && refundResult.refund) {
        metadata.refund_id = refundResult.refund.id;
        metadata.refund_amount = refundResult.refund.amount;
        metadata.refund_status = refundResult.refund.status;
      }

      await createNotification({
        title,
        message,
        type,
        order_id: order.id,
        user_id: order.user_id,
        metadata,
      });
    } catch (error) {
      console.error('Error creating order status notification:', error);
    }
  }

  /**
   * Helper function to create staff notification for order status updates
   * @private
   */
  async _createStaffOrderStatusNotification(order) {
    if (!order?.restaurant_id) return;

    let title;
    let message;
    let type;

    switch (order.status) {
      case 'preparing':
        title = 'Order Sent to Kitchen';
        message = `Order #${order.order_number || order.id.slice(0, 8)} is now being prepared`;
        type = 'order_preparing';
        break;
      case 'ready':
      case 'completed':
        title = 'Order Ready';
        message = `Order #${order.order_number || order.id.slice(0, 8)} is ready${
          order.table_number ? ` for table ${order.table_number}` : ''
        }`;
        type = 'order_ready';
        break;
      case 'rejected':
        title = 'Order Rejected';
        message = `Order #${order.order_number || order.id.slice(0, 8)} was rejected`;
        type = 'order_rejected';
        break;
      default:
        return;
    }

    try {
      await createStaffNotifications(
        {
          title,
          message,
          type,
          order_id: order.id,
          metadata: {
            order_id: order.id,
            order_number: order.order_number,
            table_number: order.table_number,
            order_status: order.status,
          },
        },
        order.restaurant_id,
      );
    } catch (error) {
      console.error('Error creating staff order status notification:', error);
    }
  }

  /**
   * Get order by ID
   */
  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase.from('orders').select('*').eq('id', orderId).single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Listen for order status changes
   */
  subscribeToOrderUpdates(orderId, callback) {
    const channelName = `order-${orderId}`;
    const existing = supabase.getChannels().find((ch) => ch.topic === `realtime:${channelName}`);
    if (existing) {
      supabase.removeChannel(existing);
    }

    const channel = supabase.channel(channelName);
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'orders',
        filter: `id=eq.${orderId}`,
      },
      (payload) => {
        if (payload.new) {
          callback(payload.new);
        }
      },
    );

    channel.subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error(`Order realtime subscription failed (${channelName}):`, status, err);
      }
    });

    return channel;
  }

  /**
   * Listen for new orders (admin side)
   */
  subscribeToNewOrders(restaurantId, callback) {
    return supabase
      .channel(`restaurant-orders-${restaurantId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => {
          callback(payload.new);
        },
      )
      .subscribe();
  }

  /**
   * Get orders by status
   */
  async getOrdersByStatus(status, restaurantId = null) {
    try {
      let query = supabase.from('orders').select('*').eq('status', status);

      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cancel order
   */
  async cancelOrder(orderId, reason, userData = null) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({
          status: 'cancelled',
          cancel_reason: reason,
          updated_at: new Date().toISOString(),
        })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;

      if (userData && data) {
        await logActivity({
          restaurant_id: data.restaurant_id,
          user_id: userData.id,
          user_name: userData.user_name || userData.full_name || 'Staff',
          user_role: userData.role || userData.profile_role || 'staff',
          action_type: 'order',
          action_name: 'cancelled',
          target_id: data.id,
          target_name: `Order #${data.order_number || data.id.slice(0, 8)}`,
          details: { reason },
          description: `Order cancelled. Reason: ${reason}`,
        });
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get orders by period (daily, weekly, monthly)
   */
  getOrdersByPeriod(orders, period) {
    const now = new Date();
    if (period === 'daily') {
      return orders.filter((order) => {
        const date = new Date(order.created_at);
        return date.toDateString() === now.toDateString();
      });
    }
    if (period === 'weekly') {
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return orders.filter((order) => {
        const date = new Date(order.created_at);
        return date >= weekStart;
      });
    }
    if (period === 'monthly') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return orders.filter((order) => {
        const date = new Date(order.created_at);
        return date >= monthStart;
      });
    }
    return [];
  }

  /**
   * Format orders for Excel export
   */
  formatOrdersForExcel(orders) {
    return orders.map((order) => ({
      ID: order.id,
      OrderNumber: order.order_number || '',
      UserID: order.user_id || '',
      CustomerName: order.customer_name || '',
      CustomerEmail: order.customer_email || '',
      CustomerPhone: order.customer_phone || '',
      TableNumber: order.table_number || '',
      Status: order.status || '',
      PaymentMethod: order.payment_method || '',
      PaymentStatus: order.payment_status || '',
      PaymentNotes: order.payment_notes || '',
      PaymentTimestamp: order.payment_timestamp
        ? new Date(order.payment_timestamp).toLocaleString()
        : '',
      Subtotal: order.subtotal || 0,
      GST: order.gst || 0,
      Discount: order.discount || 0,
      Total: parseFloat(order.total) || 0,
      RazorpayOrderID: order.razorpay_order_id || '',
      RazorpayPaymentID: order.razorpay_payment_id || '',
      RazorpaySignature: order.razorpay_signature || '',
      SpecialInstructions: order.special_instructions || '',
      CancelReason: order.cancel_reason || '',
      CreatedAt: order.created_at ? new Date(order.created_at).toLocaleString() : '',
      UpdatedAt: order.updated_at ? new Date(order.updated_at).toLocaleString() : '',
      CompletedAt: order.completed_at ? new Date(order.completed_at).toLocaleString() : '',
      Items: (order.items || []).map((i) => `${i.name || i.title} x${i.quantity || 1}`).join(', '),
    }));
  }

  /**
   * Add a total row to Excel data
   */
  addTotalRow(data) {
    if (data.length === 0) return data;
    const total = data.reduce((sum, row) => sum + (parseFloat(row.Total) || 0), 0);
    const emptyRow = {};
    Object.keys(data[0]).forEach((key) => (emptyRow[key] = ''));
    const totalRow = { ...emptyRow, Status: 'TOTAL', Total: total };
    return [...data, emptyRow, totalRow];
  }

  /**
   * Get subtotal for an order
   */
  getOrderSubtotal(order) {
    return (order.items || []).reduce(
      (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
      0,
    );
  }

  /**
   * Get counts by status for dashboard
   */
  getStatusCounts(orders) {
    const counts = {
      pending: 0,
      preparing: 0,
      ready: 0,
      completed: 0,
      rejected: 0,
    };
    orders.forEach((order) => {
      if (counts[order.status] !== undefined) {
        counts[order.status]++;
      }
    });
    return counts;
  }

  /**
   * Sync function to replay pending actions when online
   */
  async syncPendingActions() {
    if (!navigator.onLine) return;

    const pendingActions = await offlineStorage.getPendingActions();
    if (pendingActions.length === 0) return;

    const processedIds = [];

    for (const action of pendingActions) {
      try {
        if (action.type === 'UPDATE_STATUS') {
          const { orderId, status, cancelReason, userData } = action.payload;
          await this.updateOrderStatus(orderId, status, cancelReason, userData, {
            skipQueue: true,
          });
          processedIds.push(action.id);
        }
      } catch (error) {
        console.error('Failed to sync action:', action, error);
      }
    }

    if (processedIds.length > 0) {
      await offlineStorage.clearPendingActions(processedIds);
    }
  }

  /**
   * Get orders from local cache only
   */
  async getCachedOrders(restaurantId = null) {
    try {
      const cachedOrders = await offlineStorage.getOrders();
      let filtered = cachedOrders || [];
      if (restaurantId) {
        filtered = filtered.filter((o) => o.restaurant_id === restaurantId);
      }
      return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (error) {
      console.error('Failed to get cached orders:', error);
      return [];
    }
  }

  /**
   * Fetch all orders with optional restaurant filter and limit
   */
  async fetchAllOrders(restaurantId = null, limit = 200) {
    try {
      let query = supabase.from('orders').select('*');

      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      }

      const { data, error } = await query.order('created_at', { ascending: false }).limit(limit);

      if (error) throw error;

      if (data && data.length > 0) {
        await offlineStorage.saveOrders(data);
      }

      return data || [];
    } catch (error) {
      console.warn('Network request failed, falling back to offline cache:', error);
      return await this.getCachedOrders(restaurantId);
    }
  }

  /**
   * Enhanced updateOrderStatus with offline support
   */
  async updateOrderStatus(orderId, newStatus, cancelReason = null, userData = null, options = {}) {
    const status = newStatus;
    try {
      const { data: existingOrder } = await supabase
        .from('orders')
        .select('tracking')
        .eq('id', orderId)
        .maybeSingle();

      const now = new Date().toISOString();
      const tracking = { ...(existingOrder?.tracking || {}) };

      if (['confirmed', 'accepted', 'preparing'].includes(status)) {
        tracking.confirmed = { status: true, timestamp: tracking.confirmed?.timestamp || now };
      }
      if (status === 'preparing') {
        tracking.preparing = { status: true, timestamp: now };
      }
      if (status === 'ready') {
        tracking.ready = { status: true, timestamp: now };
      }
      if (status === 'completed') {
        tracking.completed = { status: true, timestamp: now };
      }

      const updates = {
        status,
        tracking,
        updated_at: now,
      };
      if (cancelReason) {
        updates.cancel_reason = cancelReason;
      }

      const { data, error } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', orderId)
        .select('*');

      if (error) throw error;
      const updatedOrder = data[0];

      await offlineStorage.updateOrder(updatedOrder);

      let refundResult = null;
      if (status === 'rejected' && updatedOrder) {
        if (updatedOrder.payment_status === 'completed' && updatedOrder.razorpay_payment_id) {
          refundResult = await this.processRefund({
            ...updatedOrder,
            cancel_reason: cancelReason || 'Order rejected by restaurant',
          });

          if (!refundResult.success) {
            console.error('Refund processing failed:', refundResult.error);
          }
        }
      }

      if (userData && updatedOrder) {
        const activityDetails = {
          old_status: null,
          new_status: status,
          cancel_reason: cancelReason,
        };

        if (refundResult) {
          activityDetails.refund_processed = refundResult.success;
          if (refundResult.refund) {
            activityDetails.refund_id = refundResult.refund.id;
            activityDetails.refund_amount = refundResult.refund.amount;
          }
          if (refundResult.error) {
            activityDetails.refund_error = refundResult.error;
          }
        }

        await logActivity({
          restaurant_id: updatedOrder.restaurant_id,
          user_id: userData.id,
          user_name: userData.user_name || userData.full_name || 'Staff',
          user_role: userData.role || userData.profile_role || 'staff',
          action_type: 'order',
          action_name: 'status_change',
          target_id: updatedOrder.id,
          target_name: `Order #${updatedOrder.order_number || updatedOrder.id.slice(0, 8)}`,
          details: activityDetails,
          description: `Order status changed to ${status}${cancelReason ? ' (Reason: ' + cancelReason + ')' : ''}${refundResult?.success ? ' - Refund processed' : ''}`,
        });
      }

      await this._createOrderStatusNotification(updatedOrder, refundResult);
      await this._createStaffOrderStatusNotification(updatedOrder);

      const customerEmail = updatedOrder.customer_email;
      const autoEmailResult = { sent: false, to: null, error: null, skippedReason: null };

      if (!customerEmail) {
        autoEmailResult.skippedReason = 'no_customer_email';
      } else {
        let restaurant = null;
        try {
          const { data: restData } = await supabase
            .from('restaurants')
            .select('*')
            .eq('id', updatedOrder.restaurant_id)
            .maybeSingle();
          restaurant = restData;
        } catch (e) {
          console.warn('[Auto email] Could not fetch restaurant:', e);
          autoEmailResult.skippedReason = 'restaurant_fetch_error';
        }

        const onlineBillingEnabled =
          restaurant?.billing_features_enabled !== false &&
          restaurant?.online_bill_enabled !== false;
        const isGuestEmail = isGuestPlaceholderEmail(customerEmail);

        if (!restaurant) {
          autoEmailResult.skippedReason = 'restaurant_not_found';
        } else if (isGuestEmail) {
          autoEmailResult.skippedReason = 'guest_placeholder_email';
        } else if (!restaurant.auto_email_billing_enabled) {
          autoEmailResult.skippedReason = 'auto_email_disabled';
        } else if (!onlineBillingEnabled) {
          autoEmailResult.skippedReason = 'online_billing_disabled';
        } else if (['preparing', 'rejected', 'completed'].includes(status)) {
          try {
            if (status === 'preparing') {
              const emailResult = await sendOrderStatusEmail(updatedOrder, restaurant, 'accepted');
              if (emailResult?.success) {
                autoEmailResult.sent = true;
                autoEmailResult.to = customerEmail;
              } else if (emailResult?.error) {
                autoEmailResult.error = emailResult.error;
              }
            } else if (status === 'rejected') {
              const emailResult = await sendOrderStatusEmail(updatedOrder, restaurant, 'rejected');
              if (emailResult?.success) {
                autoEmailResult.sent = true;
                autoEmailResult.to = customerEmail;
              } else if (emailResult?.error) {
                autoEmailResult.error = emailResult.error;
              }
            } else if (status === 'completed') {
              try {
                await sendBillToCustomer(updatedOrder, restaurant);
                autoEmailResult.sent = true;
                autoEmailResult.to = customerEmail;
              } catch (billErr) {
                autoEmailResult.error = billErr.message || String(billErr);
              }
            }
          } catch (emailErr) {
            console.warn('[Auto email] Failed to send:', emailErr);
            autoEmailResult.error = emailErr.message || String(emailErr);
          }
        }
      }

      updatedOrder._autoEmailResult = autoEmailResult;
      return updatedOrder;
    } catch (error) {
      console.warn('Update failed, checking if offline:', error);

      if (options.skipQueue) {
        console.warn('Skipping offline queue for sync action');
        throw error;
      }

      try {
        const originalOrder = await offlineStorage.getOrder(orderId);

        if (originalOrder) {
          const optimisticOrder = {
            ...originalOrder,
            status: status,
            cancel_reason: cancelReason || originalOrder.cancel_reason,
            updated_at: new Date().toISOString(),
          };

          await offlineStorage.updateOrder(optimisticOrder);

          await offlineStorage.queueAction('UPDATE_STATUS', {
            orderId,
            status,
            cancelReason,
            userData,
          });

          return optimisticOrder;
        }
      } catch (offlineErr) {
        console.error('Offline optimistic update failed:', offlineErr);
      }

      throw error;
    }
  }

  /**
   * Subscribe to all changes (INSERT, UPDATE, DELETE) for orders
   */
  subscribeToAllOrderChanges(restaurantId, callback, onStatus) {
    const channel = supabase
      .channel(`all-restaurant-orders-${restaurantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => {
          callback(payload);
        },
      )
      .subscribe(onStatus);

    return channel;
  }

  /**
   * Unsubscribe helper
   */
  unsubscribeFromOrderChanges(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }

  /**
   * Filter orders helper
   */
  filterOrders(orders, { statusFilter, searchQuery }) {
    let result = orders;

    if (statusFilter !== 'all') {
      result = result.filter((o) => o.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (o) =>
          (o.order_number || '').toLowerCase().includes(query) ||
          (o.customer_name || '').toLowerCase().includes(query) ||
          (o.customer_phone || '').includes(query) ||
          (o.id || '').toLowerCase().includes(query) ||
          (o.table_number && String(o.table_number).toLowerCase().includes(query)),
      );
    }

    return result;
  }

  /**
   * Sort orders helper
   */
  sortOrders(orders, sortBy, sortDirection) {
    return [...orders].sort((a, b) => {
      let valA, valB;

      if (sortBy === 'total') {
        valA = parseFloat(a.total || 0);
        valB = parseFloat(b.total || 0);
      } else if (sortBy === 'created_at') {
        valA = new Date(a.created_at || 0).getTime();
        valB = new Date(b.created_at || 0).getTime();
      } else {
        valA = a[sortBy];
        valB = b[sortBy];
      }

      if (valA === valB) return 0;

      if (sortDirection === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
  }

  /**
   * Get valid next status options
   */
  getNextStatusOptions(currentStatus) {
    const flow = {
      pending: ['preparing', 'rejected'],
      preparing: ['ready'],
      ready: ['completed'],
      completed: [],
      rejected: [],
      cancelled: [],
    };
    return flow[currentStatus] || [];
  }

  /**
   * Handle download preparation
   */
  handleOrderDownload(orders, period, status = null) {
    let filtered = orders;

    if (period && period !== 'all') {
      filtered = this.getOrdersByPeriod(orders, period);
    }

    if (status) {
      filtered = filtered.filter((o) => o.status === status);
    }

    const formatted = this.formatOrdersForExcel(filtered);
    const dataWithTotal = this.addTotalRow(formatted);

    let sheetName = 'Orders';
    if (status) {
      sheetName = status.charAt(0).toUpperCase() + status.slice(1);
    } else if (period) {
      sheetName = period.charAt(0).toUpperCase() + period.slice(1);
    }

    return {
      data: dataWithTotal,
      sheetName,
    };
  }
}

// Instantiate the singleton
export const orderService = new OrderService();

// Backwards-compatible bindings
export const processRefund = orderService.processRefund.bind(orderService);
export const confirmOfflinePayment = orderService.confirmOfflinePayment.bind(orderService);
export const getUserOrders = orderService.getUserOrders.bind(orderService);
export const getRestaurantOrders = orderService.getRestaurantOrders.bind(orderService);
export const getOrderById = orderService.getOrderById.bind(orderService);
export const subscribeToOrderUpdates = orderService.subscribeToOrderUpdates.bind(orderService);
export const subscribeToNewOrders = orderService.subscribeToNewOrders.bind(orderService);
export const getOrdersByStatus = orderService.getOrdersByStatus.bind(orderService);
export const cancelOrder = orderService.cancelOrder.bind(orderService);
export const getOrdersByPeriod = orderService.getOrdersByPeriod.bind(orderService);
export const formatOrdersForExcel = orderService.formatOrdersForExcel.bind(orderService);
export const addTotalRow = orderService.addTotalRow.bind(orderService);
export const getOrderSubtotal = orderService.getOrderSubtotal.bind(orderService);
export const getStatusCounts = orderService.getStatusCounts.bind(orderService);
export const syncPendingActions = orderService.syncPendingActions.bind(orderService);
export const getCachedOrders = orderService.getCachedOrders.bind(orderService);
export const fetchAllOrders = orderService.fetchAllOrders.bind(orderService);
export const updateOrderStatus = orderService.updateOrderStatus.bind(orderService);
export const subscribeToAllOrderChanges =
  orderService.subscribeToAllOrderChanges.bind(orderService);
export const unsubscribeFromOrderChanges =
  orderService.unsubscribeFromOrderChanges.bind(orderService);
export const filterOrders = orderService.filterOrders.bind(orderService);
export const sortOrders = orderService.sortOrders.bind(orderService);
export const getNextStatusOptions = orderService.getNextStatusOptions.bind(orderService);
export const handleOrderDownload = orderService.handleOrderDownload.bind(orderService);

export default orderService;
